import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import type { AlertMedia } from '../types/types';

interface MediaLightboxProps {
  media: AlertMedia[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MediaLightbox: React.FC<MediaLightboxProps> = ({
  media,
  initialIndex = 0,
  open,
  onOpenChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, open]);

  const currentMedia = media[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  };

  const handleDownload = () => {
    if (currentMedia) {
      const link = document.createElement('a');
      link.href = currentMedia.file_url;
      link.download = currentMedia.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  if (!currentMedia) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0">
        <DialogTitle className="sr-only">Media Viewer</DialogTitle>
        <div className="relative bg-black">
          {/* Close Button */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            className="absolute top-4 right-16 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
          >
            <Download className="w-6 h-6" />
          </button>

          {/* Navigation Buttons */}
          {media.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Media Content */}
          <div className="flex items-center justify-center min-h-[50vh] max-h-[85vh] p-8">
            {currentMedia.is_video ? (
              <video
                key={currentMedia.id}
                controls
                autoPlay
                className="max-w-full max-h-full"
                style={{ maxHeight: '85vh' }}
              >
                <source src={currentMedia.file_url} type={currentMedia.content_type} />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={currentMedia.file_url}
                alt={currentMedia.filename}
                className="max-w-full max-h-full object-contain"
                style={{ maxHeight: '85vh' }}
              />
            )}
          </div>

          {/* Counter and Title */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              <p className="text-sm font-medium truncate">{currentMedia.filename}</p>
              {media.length > 1 && (
                <p className="text-sm">
                  {currentIndex + 1} / {media.length}
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

